import { isSupabaseConfigured } from '../lib/supabase';
import { loadConfigFromSupabase } from './supabaseService';
import { DEFAULT_SITE_CONFIG } from '../data/defaultConfig';

interface GoogleBusinessReview {
  reviewId: string;
  reviewer: {
    profilePhotoUrl?: string;
    displayName: string;
    isAnonymous?: boolean;
  };
  starRating: number;
  comment?: string;
  createTime: string;
  updateTime: string;
  reviewReply?: {
    comment: string;
    updateTime: string;
  };
}

interface GoogleBusinessResponse {
  reviews: GoogleBusinessReview[];
  totalReviewCount: number;
  averageRating: number;
  nextPageToken?: string;
}

class GoogleBusinessService {
  private apiKey: string;
  private accountId: string;
  private locationId: string;

  constructor() {
    this.apiKey = import.meta.env.VITE_GOOGLE_BUSINESS_API_KEY || '';
    this.accountId = import.meta.env.VITE_GOOGLE_BUSINESS_ACCOUNT_ID || '';
    this.locationId = import.meta.env.VITE_GOOGLE_BUSINESS_LOCATION_ID || '';
  }

  async getReviews(pageSize: number = 10): Promise<GoogleBusinessResponse> {
    try {
      const url = `https://mybusiness.googleapis.com/v4/accounts/${this.accountId}/locations/${this.locationId}/reviews?key=${this.apiKey}&pageSize=${pageSize}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching Google Business reviews:', error);
      // Return fallback data if API fails
      return await this.getFallbackReviews();
    }
  }

  private async getFallbackReviews(): Promise<GoogleBusinessResponse> {
    try {
      // 1. Try localStorage
      const localData = localStorage.getItem('tncakes_site_config');
      if (localData) {
        const parsed = JSON.parse(localData);
        if (parsed.reviews && parsed.reviews.length > 0) {
          return this.mapConfigToResponse(parsed);
        }
      }

      // 2. Try Supabase
      if (isSupabaseConfigured) {
        const supabaseConfig = await loadConfigFromSupabase();
        if (supabaseConfig) {
          return this.mapConfigToResponse({
            ...DEFAULT_SITE_CONFIG,
            ...supabaseConfig
          });
        }
      }

      // 3. Try siteConfig.json
      try {
        const res = await fetch('/siteConfig.json');
        if (res.ok) {
          const parsed = await res.json();
          if (parsed) {
            return this.mapConfigToResponse({
              ...DEFAULT_SITE_CONFIG,
              ...parsed
            });
          }
        }
      } catch {}

    } catch (e) {
      console.error('Error parsing site config in googleBusinessService:', e);
    }

    // 4. Default codebase fallback
    return this.mapConfigToResponse(DEFAULT_SITE_CONFIG);
  }

  private mapConfigToResponse(config: any): GoogleBusinessResponse {
    return {
      reviews: (config.reviews || []).map((r: any) => ({
        reviewId: r.reviewId,
        reviewer: {
          displayName: r.displayName,
          profilePhotoUrl: r.profilePhotoUrl
        },
        starRating: r.starRating,
        comment: r.comment,
        createTime: r.createTime,
        updateTime: r.createTime
      })),
      totalReviewCount: typeof config.googleReviewCount === 'number' ? config.googleReviewCount : (parseInt(config.googleReviewCount) || 567),
      averageRating: typeof config.googleRating === 'number' ? config.googleRating : (parseFloat(config.googleRating) || 5.0)
    };
  }

  formatReviewDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60) return '1 month ago';
    return `${Math.floor(diffDays / 30)} months ago`;
  }
}

export const googleBusinessService = new GoogleBusinessService();
export type { GoogleBusinessReview, GoogleBusinessResponse };
